import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { array, number, bool, node, func } from "prop-types";

import "./srollSnap.m.css";

const ScrollSnap = ({
  children,
  defaultSelection = 0,
  activeIndex,
  isArrow = true,
  leftArrow,
  rightArrow,
  handleNextClick,
  onChange,
  NewBlock = false,
  infinite = false,
}) => {
  const noOfItems = children ? children.length : 0;
  const itemsToRender =
    infinite && noOfItems > 1 ? [children[noOfItems - 1], ...children, children[0]] : children;
  const totalRenderedItems = itemsToRender.length;

  const initialIndex = typeof activeIndex === "number" ? activeIndex : defaultSelection || 0;

  // selectedIndex here is the logical index (0 to noOfItems-1)
  const [selectedIndex, setSelectedIndex] = useState(() =>
    Math.max(0, Math.min(initialIndex, Math.max(0, noOfItems - 1)))
  );

  // renderedIndex is the index in the itemsToRender array
  const [renderedIndex, setRenderedIndex] = useState(() => (infinite && noOfItems > 1 ? selectedIndex + 1 : selectedIndex));

  const [imageHeight, setImageHeight] = useState("");
  const scroller = useRef(null);
  const scrollHandlerRef = useRef(null);
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimer = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useLayoutEffect(() => {
    if (scroller.current && infinite && noOfItems > 1) {
      jumpToItem(renderedIndex);
    }
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;

    const threshold = 50;

    if (Math.abs(diff) < threshold) return;

    if (diff > 0) {
      if (infinite || selectedIndex < noOfItems - 1) {
        nextClick();
      }
    } else if (diff < 0) {
      if (infinite || selectedIndex > 0) {
        previousClick();
      }
    }
  };



  const debounce = (func, ms) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        func(...args);
      }, ms);
    };
  };

  // measure height (use scroller height instead of brittle DOM selector)
  useEffect(() => {
    const setHeight = () => {
      if (scroller.current) setImageHeight(scroller.current.clientHeight / 2 - 90 || "");
      console.log(scroller.current.clientHeight);
    };

    setHeight();
    window.addEventListener("resize", setHeight);
    return () => window.removeEventListener("resize", setHeight);
  }, []);

  useEffect(() => {
    if (!scroller.current) return;
    const handler = debounce(() => {
      if (!scroller.current || noOfItems === 0) return;
      if (isProgrammaticScroll.current) return;
      const itemWidth = scroller.current.scrollWidth / totalRenderedItems || scroller.current.clientWidth;
      const rIndex = Math.round(scroller.current.scrollLeft / itemWidth);
      setRenderedIndex(rIndex);

      let logicalIndex = rIndex;
      if (infinite && noOfItems > 1) {
        if (rIndex === 0) {
          logicalIndex = noOfItems - 1;
        } else if (rIndex === totalRenderedItems - 1) {
          logicalIndex = 0;
        } else {
          logicalIndex = rIndex - 1;
        }
      }

      if (logicalIndex !== selectedIndex) {
        setSelectedIndex(logicalIndex);
        onChange && onChange(logicalIndex);
      }
    }, 150);


    scrollHandlerRef.current = handler;
    scroller.current.addEventListener("scroll", handler);
    return () => {
      if (scroller.current && scrollHandlerRef.current) {
        scroller.current.removeEventListener("scroll", scrollHandlerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noOfItems, children, onChange]);

  // If parent controls index via activeIndex, update to that
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (typeof activeIndex === "number" && activeIndex !== selectedIndex) {
        const targetRenderedIndex = infinite && noOfItems > 1 ? activeIndex + 1 : activeIndex;

        // Special case for wrapping forward from last to first
        if (infinite && selectedIndex === noOfItems - 1 && activeIndex === 0) {
          // Scroll to the "first-item-clone" at the end
          updateItemSelection(totalRenderedItems - 1);
          setTimeout(() => {
            // Jump to the "real-first-item" at index 1
            jumpToItem(1);
          }, 600);
        }
        // Special case for wrapping backward from first to last
        else if (infinite && selectedIndex === 0 && activeIndex === noOfItems - 1) {
          // Scroll to the "last-item-clone" at the beginning
          updateItemSelection(0);
          setTimeout(() => {
            // Jump to the "real-last-item" at index noOfItems
            jumpToItem(noOfItems);
          }, 600);
        } else {
          updateItemSelection(targetRenderedIndex);
        }

        setSelectedIndex(activeIndex);
        setRenderedIndex(targetRenderedIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, noOfItems, infinite, totalRenderedItems]);


  const smoothScroll = (node, topOrLeft, horizontal) => {
    if (!node) return;
    return node.scrollTo({
      [horizontal ? "left" : "top"]: topOrLeft,
      behavior: "smooth"
    });
  };

  const updateItemSelection = (rIndex) => {
    if (!scroller.current || totalRenderedItems === 0) return;
    isProgrammaticScroll.current = true;
    clearTimeout(programmaticScrollTimer.current);
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 600);
    const safeIndex = Math.max(0, Math.min(rIndex, totalRenderedItems - 1));
    const itemWidth = scroller.current.scrollWidth / totalRenderedItems || scroller.current.clientWidth;
    const scrollLeft = Math.floor(itemWidth * safeIndex);
    smoothScroll(scroller.current, scrollLeft, true);
  };

  const jumpToItem = (rIndex) => {
    if (!scroller.current || totalRenderedItems === 0) return;
    const itemWidth = scroller.current.scrollWidth / totalRenderedItems || scroller.current.clientWidth;
    const scrollLeft = Math.floor(itemWidth * rIndex);
    scroller.current.scrollLeft = scrollLeft;
  };


  const nextClick = (e) => {
    e && e.preventDefault();
    e && e.stopPropagation();
    if (!scroller.current || noOfItems === 0) return;

    if (infinite && noOfItems > 1) {
      if (selectedIndex === noOfItems - 1) {
        // Scroll to the clone at the end
        updateItemSelection(totalRenderedItems - 1);
        setSelectedIndex(0);
        setRenderedIndex(totalRenderedItems - 1);
        onChange && onChange(0);
        setTimeout(() => {
          jumpToItem(1);
        }, 600);
      } else {
        const newIndex = selectedIndex + 1;
        setSelectedIndex(newIndex);
        setRenderedIndex(newIndex + 1);
        updateItemSelection(newIndex + 1);
        onChange && onChange(newIndex);
      }
      handleNextClick && handleNextClick();
    } else if (selectedIndex < noOfItems - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setRenderedIndex(newIndex);
      updateItemSelection(newIndex);
      onChange && onChange(newIndex);
      handleNextClick && handleNextClick();
    }
  };


  const previousClick = (e) => {
    e && e.preventDefault();
    e && e.stopPropagation();
    if (!scroller.current || noOfItems === 0) return;

    if (infinite && noOfItems > 1) {
      if (selectedIndex === 0) {
        // Scroll to the clone at the beginning
        updateItemSelection(0);
        setSelectedIndex(noOfItems - 1);
        setRenderedIndex(0);
        onChange && onChange(noOfItems - 1);
        setTimeout(() => {
          jumpToItem(noOfItems);
        }, 600);
      } else {
        const newIndex = selectedIndex - 1;
        setSelectedIndex(newIndex);
        setRenderedIndex(newIndex + 1);
        updateItemSelection(newIndex + 1);
        onChange && onChange(newIndex);
      }
    } else if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setRenderedIndex(newIndex);
      updateItemSelection(newIndex);
      onChange && onChange(newIndex);
    }
  };


  const arrowView = () => (
    <div styleName="srollsnap-arrow-wrapper">
      {infinite || selectedIndex !== 0 ? (
        <span onClick={previousClick} styleName="arrow left-arrow" className="left-arrow">
          {leftArrow}
        </span>
      ) : null}

      {infinite || selectedIndex < noOfItems - 1 ? (
        <span onClick={nextClick} styleName="arrow right-arrow" className="right-arrow">
          {rightArrow}
        </span>
      ) : null}
    </div>

  );

  return (
    <div>
      <div styleName="wrapper" className="scroll-snap-wrapper">
        <div
          ref={scroller}
          styleName="carousel"
          className="scroll-snap-carousel"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {itemsToRender}
        </div>

        {isArrow ? arrowView() : null}
      </div>
    </div>
  );
};

ScrollSnap.propTypes = {
  isArrow: bool,
  handleNextClick: func,
  onChange: func, // parent callback
  infinite: bool
};


export default ScrollSnap;
